import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import MerchandiseOfferCarousel from '../MerchandiseCarousels/MerchandiseOfferCarousel'
import MerchandiseRecentlyViewedCarousel from '../MerchandiseCarousels/MerchandiseRecentlyViewedCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import { take } from 'lib/array/arrayUtils'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import MerchandiseBestOfBrandCarousel from '../MerchandiseCarousels/MerchandiseBestOfBrandCarousel'
import MerchandiseBestOfCategoryCarousel from '../MerchandiseCarousels/MerchandiseBestOfCategoryCarousel'
import TextLink from 'LuxKit/TextLink'
import MerchandiseSearchPageBreadcrumbs from './MerchandiseSearchPageBreadcrumbs'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

export const FullWidthContainer = styled.div`
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
  width: 100%;
  
  > * {
    margin-left: 0;
    margin-right: auto;
  }

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(60)};
    padding-right: ${rem(60)};
  }
`

const popularProductsFilters: App.MerchandiseFilters = {
  sortBy: 'recommended',
}

const FIXED_BRAND_CAROUSEL = 'Apple'
const FIXED_CATEGORY_CAROUSEL = 'Home & Garden'

interface Props {
  search?: string
  category?: App.MerchandiseCategory
  brand?: App.MerchandiseBrand
  /**
   * Whether to show breadcrumbs at the top
   * @default true
   */
  showBreadcrumbs?: boolean
}

function MerchandiseSearchPageNoResults(props: Props) {
  const { search, category, brand, showBreadcrumbs = true } = props
  const intl = useIntl()

  const [parentCategory] = useMerchandiseCategory({ id: category?.parentId })
  const [topCategories] = useMerchandiseTopCategories()
  const [childCategories] = useMerchandiseCategories({
    categoryIds: category?.childCategoryIds,
  }, { disabled: !category?.childCategoryIds })

  const popularFilters = useMemo((): App.MerchandiseFilters | undefined => {
    if (category) {
      if (category.parentId) {
        return {
          categories: [category.parentId],
        }
      } else if (topCategories.length > 0) {
        const aTopCategory = topCategories.find(cat => cat.id !== category.id)
        if (aTopCategory) {
          return {
            // just take the first top category that isn't ours
            categories: [aTopCategory.id],
          }
        }
      }
    }
  }, [category, topCategories])

  const relatedCategoryFilters = useMemo(() => {
    return childCategories.map(cat => ({
      name: cat.name,
      filter: {
        categories: [cat.id],
      },
    }))
  }, [childCategories])

  const trackingPageName = 'Search page no results'
  const searchForLabel = category?.name ?? brand?.name ?? search

  const headingText = searchForLabel
    ? intl.formatMessage(
        {
          id: 'pEuKVqdAQi',
          defaultMessage: 'No results found for "{searchFor}"',
          description: 'Title for merchandise search no results page with search term',
        },
        { searchFor: searchForLabel },
      )
    : intl.formatMessage({
        id: 'QuQ4jkdMIK',
        defaultMessage: 'No results found',
        description: 'Title for merchandise search no results page',
      })

  return <>
    {showBreadcrumbs && <FullWidthContainer>
      <MerchandiseSearchPageBreadcrumbs />
    </FullWidthContainer>}
    <LayoutContainer>
      <Sheet padding="20 0 64 0">
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            {headingText}
          </Heading>
          <VerticalSpacer gap={8}>
            <BodyText variant="large">
              <FormattedMessage
                id="mNpBtJqhps"
                defaultMessage="It might've moved or is no longer available."
                description="Subtitle explaining why no results were found"
              />
            </BodyText>
            <BodyText variant="large">
              <List>
                <li>
                  <FormattedMessage
                    id="A7dEM5iHfS"
                    defaultMessage="Check your spelling or;"
                    description="Suggestion to check spelling"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="bxF3qrVkk3"
                    defaultMessage="Try searching by product name or category"
                    description="Suggestion to search by product name or category"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="9jNkbT8DCG"
                    defaultMessage="You can also <categoryLink>shop by category</categoryLink> or <brandLink>shop by brand</brandLink>"
                    description="Suggestion with links to shop by category or brand"
                    values={{
                      categoryLink: (chunks: React.ReactNode) => (
                        <TextLink to="/shop/categories">{chunks}</TextLink>
                      ),
                      brandLink: (chunks: React.ReactNode) => (
                        <TextLink to="/shop/brands">{chunks}</TextLink>
                      ),
                    }}
                  />
                </li>
              </List>
            </BodyText>
            <BodyText variant="large">
              <FormattedMessage
                id="PrR3By+Ht9"
                defaultMessage="There's plenty more to discover in Luxury Escapes Shop."
                description="Encouragement to explore more products"
              />
            </BodyText>
          </VerticalSpacer>
        </VerticalSpacer>
      </Sheet>
    </LayoutContainer>
    <MerchandiseRecentlyViewedCarousel trackingPageName={trackingPageName} />
    <MerchandiseOfferCarousel
      title={
        <FormattedMessage
          id="XD0Rs8ZgQq"
          defaultMessage="Top products <em>for you</em>"
          description="Title for top products carousel"
          values={{
            em: (chunks: React.ReactNode) => <em>{chunks}</em>,
          }}
        />
      }
      filters={popularProductsFilters}
      viewAllTo="/search/shop"
      trackingListName={intl.formatMessage({
        id: 'lUSWmtG9kt',
        defaultMessage: 'Top products for you',
        description: 'Tracking name for top products carousel',
      })}
      trackingPageName={trackingPageName}
    />
    <MerchandiseBestOfBrandCarousel
      trackingPageName={trackingPageName}
      brandName={FIXED_BRAND_CAROUSEL}
    />
    <MerchandiseBestOfCategoryCarousel
      trackingPageName={trackingPageName}
      categoryName={FIXED_CATEGORY_CAROUSEL}
    />
    {take(relatedCategoryFilters, 2).map(filter => <MerchandiseOfferCarousel
      key={filter.name}
      title={intl.formatMessage(
        {
          id: 'p8NshuJYr6',
          defaultMessage: 'Explore more from {categoryName}',
          description: 'Title for explore more from category carousel',
        },
        { categoryName: filter.name },
      )}
      filters={filter.filter}
      trackingListName={intl.formatMessage(
        {
          id: '/Lm1iEby3a',
          defaultMessage: 'Explore more from {categoryName}',
          description: 'Tracking name for explore more from category carousel',
        },
        { categoryName: filter.name },
      )}
      trackingPageName={trackingPageName}
    />)}
    {parentCategory && <MerchandiseOfferCarousel
      title={intl.formatMessage(
        {
          id: 'qYGmoyfOqp',
          defaultMessage: 'Popular in {categoryName}',
          description: 'Title for popular in category carousel',
        },
        { categoryName: parentCategory.name },
      )}
      filters={popularFilters}
      trackingListName={intl.formatMessage(
        {
          id: 'aID+FMnLN3',
          defaultMessage: 'Popular in {categoryName}',
          description: 'Tracking name for popular in category carousel',
        },
        { categoryName: parentCategory.name },
      )}
      trackingPageName={trackingPageName}
    />}
  </>
}

export default MerchandiseSearchPageNoResults
